import styled, { css } from 'styled-components';
import {
    ANIMATION_CURVE,
    FOCUS_OUTLINE_STYLE,
    getSpacingToken,
    getTextTokenPreset,
    getToken,
    Size,
} from '@optimus-web/common';
import { getCheckboxToken } from './tokens';
import { CheckboxProps } from './Checkbox';

const transitionDuration = '90ms';

const getHiddenInputPosition = (size: CheckboxInputProps['$size'], hasChildren: boolean) => {
    if (!hasChildren) {
        switch (size) {
            case 'large':
                return css`
                    top: 0px;
                    left: 0px;
                `;
            default: // small and medium
                return css`
                    top: -4px;
                    left: -4px;
                `;
        }
    }

    switch (size) {
        case 'small':
            return css`
                top: 2px;
                left: -4px;
            `;
        case 'large':
            return css`
                top: 8px;
                left: 0;
            `;
        default: // medium size
            return css`
                top: 4px;
                left: -4px;
            `;
    }
};

export const CheckboxContainer = styled.div``;

interface CheckboxElementProps {
    disabled?: boolean;
    withoutLabel?: boolean;
}

export const InputLabel = styled.label<CheckboxElementProps>`
    display: inline-flex;
    position: relative;
    color: ${getToken('textStaticPrimary')};

    ${(props) =>
        !props.withoutLabel &&
        css`
            padding: ${getSpacingToken('50')}px 0;
        `}

    ${(props) =>
        props.disabled &&
        css`
            color: ${getToken('textDisabled')};
        `}
`;

/*
 * We are using the hidden input to make the clickable/tappable area 24px wide and tall.
 * Needed for accessibility.
 */
interface CheckboxInputProps {
    $size: CheckboxProps['size'];
    hasChildren: boolean;
}

export const CheckboxElement = styled.input<CheckboxInputProps>`
    position: absolute;
    ${({ $size, hasChildren }) => getHiddenInputPosition($size, hasChildren)}
    height: ${getSpacingToken('300')}px;
    width: ${getSpacingToken('300')}px;
    margin: 0;
    padding: 0;
    opacity: 0;
`;

export interface CheckMarkBackgroundElementProps {
    checked?: boolean;
    disabled?: boolean;
    error?: boolean;
    size?: Exclude<Size, 'extraLarge'>;
    indeterminate?: boolean;
    withoutLabel?: boolean;
}

const getCheckboxSize = (size?: Exclude<Size, 'extraLarge'>) => {
    if (size === 'large') {
        return getSpacingToken('300');
    }

    return getSpacingToken('200');
};

export const CheckMarkBackgroundElement = styled.button<CheckMarkBackgroundElementProps>`
    display: inline-flex;
    position: relative;
    align-items: center;
    justify-content: center;
    width: ${({ size }) => getCheckboxSize(size)}px;
    height: ${({ size }) => getCheckboxSize(size)}px;
    min-width: ${({ size }) => getCheckboxSize(size)}px;
    padding: 0;
    border: none;
    background-color: transparent;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    transition: background-color ${transitionDuration} ${ANIMATION_CURVE.fastOutLinearIn};

    &::before {
        content: '';
        background-color: ${getCheckboxToken('backgroundColor')};
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        border: ${getToken('borderWidth100')}px solid ${getCheckboxToken('borderColor')};
        border-radius: ${({ size }) => getToken(size === 'large' ? 'borderRadius50' : 'borderRadius25')}px;
        box-sizing: border-box;
        transition:
            border-color ${transitionDuration} ${ANIMATION_CURVE.fastOutLinearIn},
            background-color ${transitionDuration} ${ANIMATION_CURVE.fastOutLinearIn};
    }

    ${({ error }) =>
        error &&
        css`
            &::before {
                outline: ${getToken('borderWidth200')}px solid ${getToken('borderInteractiveInputError')};
                outline-offset: -${getToken('borderWidth200')}px;
            }
        `}

    &:focus-visible {
        ${FOCUS_OUTLINE_STYLE}
        border-radius: ${getToken('borderRadius25')}px;
    }

    &:hover {
        &::before {
            border-color: ${getCheckboxToken('borderColor', 'hover')};
            background-color: ${getCheckboxToken('backgroundColor', 'hover')};
        }
    }

    &:active {
        &::before {
            border-color: ${getCheckboxToken('borderColor', 'active')};
            background-color: ${getCheckboxToken('backgroundColor', 'active')};
        }
    }

    ${({ size, withoutLabel }) =>
        !withoutLabel &&
        css`
            margin-top: ${size === 'small' ? getSpacingToken('25') : getSpacingToken('50')}px;
        `}

    ${({ disabled, indeterminate }) =>
        indeterminate &&
        css`
            &::after {
                content: '';
                position: absolute;
                width: 10px;
                height: 2px;
                left: calc(50% - 10px / 2);
                top: calc(50% - 2px / 2);
                background-color: ${disabled ? getToken('textDisabled') : getToken('textStaticInverse')};
                border-radius: 1px;
                z-index: 2;
            }
        `}
`;

interface CheckMarkSvgElementProps {
    checked?: boolean;
    indeterminate?: boolean;
}

export const CheckMarkSvgElement = styled.svg<CheckMarkSvgElementProps>`
    opacity: 0;
    transition: opacity ${transitionDuration} ${ANIMATION_CURVE.linearOutSlowIn};
    position: relative;
    z-index: 1;

    ${({ checked, indeterminate }) =>
        checked &&
        !indeterminate &&
        css`
            opacity: 1;
        `}
`;

interface CheckMarkPathElementProps {
    checked?: boolean;
    disabled?: boolean;
}

export const CheckMarkPathElement = styled.path<CheckMarkPathElementProps>`
    fill: ${({ disabled }) => (disabled ? getToken('textDisabled') : getToken('textStaticInverse'))};
`;

interface LabelElementProps {
    size?: Exclude<Size, 'extraLarge'>;
    required?: boolean;
}

export const LabelElement = styled.div<LabelElementProps>`
    ${({ size }) => (size === 'small' ? getTextTokenPreset('bodyMedium') : getTextTokenPreset('bodyLarge'))}
    align-self: flex-end;
    padding-left: ${getSpacingToken('150')}px;
`;
